import { parseChecks } from './checks'
import { usablePlaceCoord } from './geocode'
import { mergeTrackParts } from './gpx'
import { newId } from './id'
import { asArray, isRecord, num, str } from './json'
import { MAX_VIA } from './route'
import { surfaceFromRaw } from './surface'
import { currentCategory, emptyDiary, EMPTY_TRIP, renumber } from './trip'
import { isTulipShape, normalizeTurn } from './tulip'
import { MAX_ABSAGEN, stelleAus } from './tulipMerge'
import { altesFahrzeug, leseFahrzeug } from './vehicle'
import { weatherFromRaw } from './weather'
import type {
  Camping,
  Checks,
  DiaryEntry,
  DiaryPost,
  DiaryTrack,
  Expense,
  LatLon,
  SerpentineRun,
  Stage,
  StageRoute,
  StageType,
  TrackElevation,
  TrackFix,
  TrackPart,
  TrackProfilePoint,
  TrackStop,
  Trip,
  TripMeta,
  Tulip,
  Vehicle,
  Waypoint,
} from './types'
import { STAGE_TYPES } from './types'

/**
 * Import und Export als eine Datei.
 *
 * Der Import versteht zwei Formate: das eigene und das des ursprünglichen
 * Einzeldatei-Prototyps. Letzteres, weil genau daraus die ersten echten Daten
 * kommen und ein Umbau, der die Vorarbeit wegwirft, keiner ist.
 */

export const EXPORT_FORMAT = 'roadbook'
/**
 * Fassung des eigenen Formats.
 *
 * 2 war es, seit die Checklisten ihre Einträge selbst enthalten (Listen statt
 * Haken je Name, siehe checks.ts). Der Sprung war nötig, obwohl das Lesen beide
 * Formate versteht: eine ältere Fassung der App würde die Liste für ein
 * kaputtes Feld halten und alle Haken stillschweigend verlieren.
 *
 * 3, seit ein Tagebucheintrag den aufgezeichneten Weg mitbringen kann
 * (DiaryEntry.track). Dieselbe Überlegung: eine ältere Fassung kennt das Feld
 * nicht, liest darüber hinweg und schriebe beim nächsten Export eine Datei
 * ohne die Tracks - der Verlust fällt niemandem auf. Mit der 3 lehnt sie die
 * Datei ab und sagt, dass sie zu alt ist.
 *
 * 4, seit eine Etappe Tulpen mitbringen kann (Stage.tulips). Genau dieselbe
 * Überlegung ein drittes Mal, und hier wiegt sie am schwersten: die Tulpen
 * entstehen unterwegs, oft auf dem Telefon der Beifahrerin, und werden per
 * Datei auf das zweite Gerät gebracht. Eine ältere Fassung, die das Feld
 * nicht kennt, gäbe die Datei kommentarlos ohne die Skizzen zurück - und
 * bemerkt hätte das niemand, bis die Seite im Buch leer bleibt.
 *
 * 5, seit ein Track aus mehreren Dateien bestehen kann (DiaryTrack.parts). Zum
 * vierten Mal dieselbe Überlegung, und diesmal geht mehr verloren als ein
 * Feld: eine ältere Fassung liest die Summe, ignoriert die Teile und schreibt
 * beim nächsten Export einen Track, der aussieht wie einer aus einer einzigen
 * Datei. Die Kilometer stimmen dann noch, aber der Vormittag und der Nachmittag
 * lassen sich nicht mehr auseinandernehmen - und wer die falsche Datei
 * eingelesen hat, bekommt sie nicht mehr einzeln heraus.
 *
 * 6, seit das Tagebuch aus Beiträgen mit Verfasser besteht (posts) statt aus
 * einem Textfeld je Tag. Zum fünften Mal dieselbe Überlegung, und diesmal geht
 * das meiste verloren: eine ältere Fassung kennt die Liste nicht, liest über
 * sie hinweg und schreibt beim nächsten Export eine Datei, in der das gesamte
 * Tagebuch fehlt - denn der alte Ort dafür, DiaryEntry.text, ist nach der
 * Überführung leer. Aus einer Sicherung, die aussieht wie eine vollständige,
 * käme dann eine Reise ohne einen einzigen Abend zurück. Mit der 6 lehnt die
 * ältere Fassung die Datei ab und sagt, dass sie zu alt ist.
 *
 * In die andere Richtung passt es weiterhin: eine Datei der Fassungen 1 bis 5
 * bringt ihren Text in DiaryEntry.text mit, und der wird beim Lesen zu einem
 * Beitrag wie jeder alte Bestand auch (lib/diary.ts).
 *
 * 7, seit ein Übernachtungsplatz seine eigene Koordinate tragen kann
 * (Camping.at). Zum sechsten Mal dieselbe Überlegung: eine ältere Fassung
 * kennt das Feld nicht, liest darüber hinweg und schreibt beim nächsten Export
 * die Plätze ohne ihre Lage. Bemerkt hätte das niemand - bis abends das
 * Navigationsgerät wieder ins Ortszentrum führt statt auf den Platz, und genau
 * dafür wurden die Koordinaten eingetragen.
 *
 * 8, seit eine Etappe Zwischenpunkte tragen kann (Stage.via). Eine ältere
 * Fassung kennt das Feld nicht, liest darüber hinweg und schreibt beim
 * nächsten Export eine Etappe ohne ihre Zwischenpunkte. Die nächste Berechnung
 * schickte die Reisenden dann wieder durch den Karawankentunnel statt über den
 * Vršič - und die einzige Spur davon wäre eine Kilometerzahl, die niemand
 * nachrechnet.
 *
 * 9, seit ein Tagebucheintrag das Wetter seiner Fahrt mitbringen kann
 * (DiaryEntry.weatherFromTrack) und ein Track die Punkte, aus denen es geholt
 * wurde (DiaryTrack.fixes). Zum achten Mal dieselbe Überlegung, und sie wiegt
 * hier doppelt: eine ältere Fassung schriebe die Werte weg, und die Punkte
 * gleich mit - danach liesse sich das Wetter dieses Tages auch nicht wieder
 * holen, denn die Rohpunkte der Aufzeichnung gibt es längst nicht mehr. Aus
 * einer Sicherung, die aussieht wie eine vollständige, käme eine Reise zurück,
 * der man diesen Verlust nicht ansieht.
 *
 * 10, seit der Katalog der Tulpen zwei Formen mehr hat (faehre und schotter,
 * siehe lib/tulip.ts). Die neunte Wiederholung derselben Überlegung, und sie
 * greift diesmal aus einem anderen Grund: nicht ein neues Feld, sondern ein
 * neuer Wert in einem alten. stageTulips wirft eine Tulpe weg, deren Form es
 * nicht kennt - ausdrücklich und mit Begründung, denn eine Skizze, die eine
 * Kreuzung behauptet, wo in der Datei eine Fähre stand, ist schlechter als
 * keine. Genau das macht den Verlust hier still: eine ältere Fassung liest die
 * Datei, verwirft die Fähren und den Schotter zeilenweise, und schreibt beim
 * nächsten Export eine Etappe, in der diese Zeilen fehlen. Auf dem Papier bleibt
 * dann die eine Zeile weg, die einen Termin hatte.
 *
 * Dass es die Fassung überhaupt braucht, ist der Unterschied zwischen einem
 * zusätzlichen Feld und einem zusätzlichen Wert: bei einem Feld hätte man es
 * am fehlenden Schlüssel gemerkt, bei einem Wert sieht die Datei tadellos aus.
 * Mit der 10 lehnt die ältere Fassung sie ab und sagt, dass sie zu alt ist -
 * und das ist die einzige Stelle, an der das noch gesagt werden kann.
 *
 * 11, seit die Reise ein Fahrzeug trägt (Trip.vehicle). Die zehnte Wiederholung
 * derselben Überlegung, und sie ist heimtückischer als die neun davor: Name und
 * Verbrauch stehen weiterhin auch in den alten Feldern (siehe mitFahrzeug in
 * lib/vehicle.ts), eine ältere Fassung liest also eine Datei, die vollständig
 * aussieht, und schreibt eine zurück, die es nicht ist. Weg wären
 * Kraftstoffart, Tankinhalt, Nationalitätszeichen und der Zeitzuschlag - und
 * mit ihm die 25 Prozent, ohne die ein beladener Bus eine Stunde zu früh
 * ankommt, beziehungsweise die 0 Prozent, mit denen ein Personenwagen nicht
 * mehr ein Viertel zu langsam plant. Auffallen würde das an einer Fahrzeit, die
 * niemand nachrechnet, und an einer Ausgabenkategorie, die wieder „Benzin“
 * heisst, obwohl ein Diesel unterwegs ist.
 *
 * 12, seit die Tulpenvorschläge ein Gedächtnis haben: die Herkunft einer
 * übernommenen Tulpe (Tulip.src) und die abgelehnten Stellen einer Etappe
 * (Stage.tulipsRejected, beides in lib/tulipMerge.ts). Die elfte Wiederholung
 * derselben Überlegung, und sie ist von der stillen Sorte: eine ältere Fassung
 * liest die Datei, überliest beide Felder und schreibt beim nächsten Export
 * Tulpen ohne Herkunft und eine Etappe ohne ihre Absagen zurück. Der Datei
 * sieht man das nicht an - jede Zeile steht noch da. Bemerkt wird es erst beim
 * nächsten Druck auf „Tulpen vorschlagen“: alles, was längst im Buch steht,
 * wird wieder angeboten, angehakt, und alles, was jemand begründet
 * weggeworfen hat, gleich mit. Wer dann übernimmt, hat jede Anweisung zweimal
 * im gedruckten Roadbook - und eine doppelte Tulpe ist bei Tempo 80 schlimmer
 * als eine fehlende.
 */
export const EXPORT_VERSION = 12

/** Ein Foto im Austauschformat: Metadaten plus Bild als Data-URL. */
export interface TransferPhoto {
  id: string
  stageId: string
  createdAt: number
  dataUrl: string
}

export interface Bundle {
  trip: Trip
  diary: DiaryEntry[]
  /**
   * Die Tagebuchbeiträge, über alle Etappen hinweg.
   *
   * Eine flache Liste und keine Gruppierung je Etappe: jeder Beitrag trägt
   * seine stageId ohnehin, und eine zweite Stelle, an der dieselbe Zuordnung
   * steht, könnte ihr widersprechen. Genauso wie bei den Fotos.
   */
  posts: DiaryPost[]
  expenses: Expense[]
  checks: Checks
  photos: TransferPhoto[]
}

export interface ExportFile extends Bundle {
  format: typeof EXPORT_FORMAT
  version: number
  exportedAt: string
}

/** Fehler mit einer Meldung, die man dem Nutzer zeigen kann. */
export class ImportError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ImportError'
  }
}

/**
 * Liest eine Exportdatei und bringt sie auf das aktuelle Modell.
 *
 * @throws ImportError wenn die Datei nicht als Roadbook erkennbar ist.
 */
export function parseBundle(raw: unknown): Bundle {
  if (!isRecord(raw)) throw new ImportError('Die Datei enthält kein Roadbook.')

  if (raw.format === EXPORT_FORMAT) return parseCurrent(raw)
  if (isRecord(raw.trip) && Array.isArray((raw.trip as Record<string, unknown>).stages)) {
    return parseLegacy(raw)
  }
  throw new ImportError('Unbekanntes Format. Erwartet wird ein Roadbook-Export.')
}

function parseCurrent(raw: Record<string, unknown>): Bundle {
  const version = num(raw.version, 0)
  if (version > EXPORT_VERSION) {
    throw new ImportError(
      `Die Datei stammt aus einer neueren Fassung (Version ${version}). Bitte zuerst die App aktualisieren.`,
    )
  }

  // Die Reise entscheidet, ob die alten Kategorienamen umgeschrieben werden:
  // eine Datei ohne Fahrzeug stammt aus der Zeit, aus der die Umbenennung
  // kommt. Sie wird deshalb zuerst gelesen.
  const trip = parseTrip(raw.trip)

  return {
    trip,
    diary: asArray(raw.diary).map(parseDiary).filter(hasStage),
    posts: asArray(raw.posts).map(parsePost).filter(hasText),
    expenses: asArray(raw.expenses).map((e) => parseExpense(e, trip)),
    checks: parseChecks(raw.checks),
    photos: asArray(raw.photos).map(parsePhoto).filter(hasDataUrl),
  }
}

/**
 * Das Format des Prototyps: andere Feldnamen, Tagebuch und Fotos als Objekte
 * statt Listen, Fotos direkt als Data-URLs ohne eigene ID.
 */
function parseLegacy(raw: Record<string, unknown>): Bundle {
  const legacyTrip = isRecord(raw.trip) ? raw.trip : {}
  const legacyMeta = isRecord(legacyTrip.meta) ? legacyTrip.meta : {}

  const stages = asArray(legacyTrip.stages).map((entry, index): Stage => {
    const s = isRecord(entry) ? entry : {}
    // Der Prototyp kannte weder berechnete Strecken noch Tulpen oder
    // Zwischenpunkte. Gelesen werden die Felder hier trotzdem, denn eine Datei
    // kann durch beide Formate gegangen sein.
    const route = stageRoute(s.route)
    const tulips = stageTulips(s.tulips)
    const abgesagt = stageTulipsRejected(s.tulipsRejected)
    const via = stageVia(s.via)
    return {
      id: str(s.id) || newId('s'),
      day: num(s.day, index + 1),
      from: str(s.from),
      to: str(s.to),
      c1: coord(s.c1),
      c2: coord(s.c2),
      ...(via.length > 0 ? { via } : {}),
      km: num(s.km, 0),
      h: num(s.h, 0),
      land: str(s.land),
      type: stageType(s.type),
      camping: asArray(s.camping).map((c) => {
        const item = isRecord(c) ? c : {}
        return { name: str(item.n), price: str(item.p), note: str(item.note) }
      }),
      highlights: asArray(s.highlights).map(str).filter(Boolean),
      night: asArray(s.night).map(str).filter(Boolean),
      tip: str(s.tip),
      ...(route ? { route } : {}),
      ...(tulips.length > 0 ? { tulips } : {}),
      ...(abgesagt.length > 0 ? { tulipsRejected: abgesagt } : {}),
      ...(s.optional ? { optional: true } : {}),
      ...(s.skip ? { skip: true } : {}),
    }
  })

  const trip: Trip = {
    meta: {
      // Was nicht in der Datei steht, bleibt leer. Hier standen einmal die
      // Werte der Beispielreise, und damit kam eine Datei ohne Titel als
      // "Schweiz → Balkan → Schwarzes Meer" im VW T3 zurück - siehe
      // EMPTY_TRIP in trip.ts.
      title: str(legacyMeta.title),
      vehicle: str(legacyMeta.vehicle),
      crew: num(legacyMeta.crew, EMPTY_TRIP.meta.crew),
      start: str(legacyMeta.start),
      consumption: num(legacyMeta.consumption, EMPTY_TRIP.meta.consumption),
      // Im Prototyp hiess das Feld dieselPrice.
      fuelPrice: num(legacyMeta.dieselPrice, EMPTY_TRIP.meta.fuelPrice),
      currency: 'EUR',
    },
    // Keine Etappen heisst keine Etappen. Hier stand die Vorlage, und damit
    // brachte der Import einer selbst exportierten leeren Reise sechzehn
    // Balkanetappen mit - dieselbe Überlegung wie bei den leeren Listen in
    // parseVignettes: eine leere Liste ist eine Aussage.
    stages: renumber(stages),
  }

  const diary: DiaryEntry[] = []
  if (isRecord(raw.diary)) {
    for (const [stageId, value] of Object.entries(raw.diary)) {
      const d = isRecord(value) ? value : {}
      diary.push({
        stageId,
        done: Boolean(d.done),
        date: str(d.date),
        odo: str(d.odo),
        realKm: str(d.realKm),
        // Im Prototyp hiess das Feld wx.
        weather: str(d.wx),
        text: str(d.text),
      })
    }
  }

  // Der Prototyp kannte kein Fahrzeug - seine Reise trägt keines, und damit ist
  // sie für currentCategory das, was sie ist: ein Bestand aus der Zeit, aus der
  // die Umbenennung der Kategorien stammt.
  const expenses = asArray(raw.expenses).map((entry): Expense => {
    const e = isRecord(entry) ? entry : {}
    return {
      id: str(e.id) || newId('e'),
      category: currentCategory(str(e.cat) || 'Sonstiges', trip),
      amount: num(e.amt, 0),
      note: str(e.note),
      stageId: str(e.stage),
      date: '',
    }
  })

  // Der Prototyp nannte die Vignetten vig und kannte nur Haken je Name - also
  // genau das alte Format, das parseChecks ohnehin überführen muss.
  const legacyChecks = isRecord(raw.checks) ? raw.checks : {}
  const checks: Checks = parseChecks({ vignettes: legacyChecks.vig, kit: legacyChecks.kit })

  const photos: TransferPhoto[] = []
  if (isRecord(raw.photos)) {
    for (const [stageId, list] of Object.entries(raw.photos)) {
      for (const dataUrl of asArray(list)) {
        if (typeof dataUrl !== 'string' || !dataUrl.startsWith('data:')) continue
        photos.push({ id: newId('p'), stageId, createdAt: Date.now(), dataUrl })
      }
    }
  }

  // Der Prototyp kannte keine Beiträge - sein Tagebuch stand als Text am
  // Eintrag, und genau von dort holt ihn die Überführung ab.
  return { trip, diary, posts: [], expenses, checks, photos }
}

function parseTrip(raw: unknown): Trip {
  if (!isRecord(raw)) return EMPTY_TRIP
  const meta = isRecord(raw.meta) ? raw.meta : {}

  const stages = asArray(raw.stages).map((entry, index): Stage => {
    const s = isRecord(entry) ? entry : {}
    const route = stageRoute(s.route)
    const tulips = stageTulips(s.tulips)
    const abgesagt = stageTulipsRejected(s.tulipsRejected)
    const via = stageVia(s.via)
    return {
      id: str(s.id) || newId('s'),
      day: num(s.day, index + 1),
      from: str(s.from),
      to: str(s.to),
      c1: coord(s.c1),
      c2: coord(s.c2),
      ...(via.length > 0 ? { via } : {}),
      km: num(s.km, 0),
      h: num(s.h, 0),
      land: str(s.land),
      type: stageType(s.type),
      camping: asArray(s.camping).map(campingPlatz),
      highlights: asArray(s.highlights).map(str).filter(Boolean),
      night: asArray(s.night).map(str).filter(Boolean),
      tip: str(s.tip),
      ...(route ? { route } : {}),
      ...(tulips.length > 0 ? { tulips } : {}),
      ...(abgesagt.length > 0 ? { tulipsRejected: abgesagt } : {}),
      ...(s.optional ? { optional: true } : {}),
      ...(s.skip ? { skip: true } : {}),
    }
  })

  const rahmen: TripMeta = {
    // Wie in parseLegacy: gelesen wird, was dasteht. Eine bewusst leere
    // Reise soll auch als leere Reise zurückkommen.
    title: str(meta.title),
    vehicle: str(meta.vehicle),
    crew: num(meta.crew, EMPTY_TRIP.meta.crew),
    start: str(meta.start),
    consumption: num(meta.consumption, EMPTY_TRIP.meta.consumption),
    fuelPrice: num(meta.fuelPrice, EMPTY_TRIP.meta.fuelPrice),
    currency: str(meta.currency) || 'EUR',
  }

  const auto = fahrzeugAusDatei(raw, rahmen)

  return {
    meta: rahmen,
    ...(auto ? { vehicle: auto } : {}),
    // Keine Etappen heisst keine Etappen. Hier stand die Vorlage, und damit
    // brachte der Import einer selbst exportierten leeren Reise sechzehn
    // Balkanetappen mit - dieselbe Überlegung wie bei den leeren Listen in
    // parseVignettes: eine leere Liste ist eine Aussage.
    stages: renumber(stages),
  }
}

/**
 * Das Fahrzeug einer Importdatei.
 *
 * @returns undefined nur dann, wenn die Datei kein Fahrzeugfeld trägt.
 *
 * Die Unterscheidung ist dieselbe wie in lib/vehicle.ts, und sie ist der Grund,
 * aus dem hier eine eigene Funktion steht: ein fehlendes Feld und ein
 * vorhandenes, aber unlesbares Feld sind zwei verschiedene Auskünfte.
 *
 * Fehlt das Feld, wird keines erfunden: eine Datei aus der Zeit davor
 * beschreibt ihr Fahrzeug in den alten Feldern, und genau daraus macht
 * fahrzeug() eines - mit den Annahmen von damals, nicht mit Nullen von heute.
 * Diese Reise ist Altbestand, und nur für sie schreibt currentCategory die
 * alten Kategorienamen um (siehe CATEGORY_RENAMES in lib/seed.ts).
 *
 * Steht dagegen etwas Unlesbares im Feld - eine Zeichenkette statt eines
 * Objekts, aus einer Handkorrektur oder einer fremden Fassung -, dann trägt die
 * Reise ein Fahrzeug, auch wenn dessen Angaben nichts hergeben. Sie ganz ohne
 * Fahrzeugfeld einzulesen machte aus ihr Altbestand, und eine „Diesel“-Buchung
 * hiesse danach still „Benzin“. Sie bekommt deshalb dasselbe Ersatzfahrzeug,
 * das fahrzeug() und ueberfuehreFahrzeug() ihr im Bestand ohnehin geben - eine
 * Datei, die durch den Import läuft, sieht danach aus wie eine Reise, die
 * dieselbe App geöffnet hat.
 */
function fahrzeugAusDatei(raw: Record<string, unknown>, meta: TripMeta): Vehicle | undefined {
  const gelesen = leseFahrzeug(raw.vehicle)
  if (gelesen) return gelesen
  if (Object.prototype.hasOwnProperty.call(raw, 'vehicle')) return altesFahrzeug(meta)
  return undefined
}

function parseDiary(raw: unknown): DiaryEntry {
  const d = isRecord(raw) ? raw : {}
  const base = emptyDiary(str(d.stageId))
  const track = diaryTrack(d.track)
  // Das Wetter gehört zur Aufzeichnung: ohne Track ist es eine Auskunft über
  // eine Fahrt, die in dieser Datei nicht steht, und der Vorschlag daraus wäre
  // genau die plausible Zahl ohne Grundlage, die es nicht geben soll.
  const wetter = track ? weatherFromRaw(d.weatherFromTrack) : undefined
  return {
    ...base,
    done: Boolean(d.done),
    date: str(d.date),
    odo: str(d.odo),
    realKm: str(d.realKm),
    weather: str(d.weather),
    text: str(d.text),
    ...(track ? { track } : {}),
    ...(wetter ? { weatherFromTrack: wetter } : {}),
  }
}

/**
 * Der aufgezeichnete Weg eines Tagebucheintrags aus einer Importdatei.
 *
 * Fehlt er, ist es keiner: Dateien aus der Zeit vor dem Feld kennen ihn nicht,
 * und die allermeisten Etappen haben keine Aufzeichnung.
 *
 * Ein unbrauchbarer Stützpunkt verwirft den ganzen Track, genau wie bei der
 * berechneten Strasse: eine Linie, die einen Punkt überspringt, nimmt eine
 * Abkürzung, die niemand gefahren ist. Ein unbrauchbarer Halt dagegen fällt
 * nur selbst heraus - die Zeitleiste ist eine Aufzählung, und eine Zeile
 * weniger erfindet nichts.
 */
function diaryTrack(v: unknown): DiaryTrack | undefined {
  const ganzes = trackKern(v)
  if (!ganzes) return undefined

  // Die Teile sind die Wahrheit, sobald es welche gibt: alles darüber ist aus
  // ihnen gerechnet. Deshalb wird der Track hier nicht aus der Datei
  // übernommen, sondern aus den Teilen neu zusammengelegt - eine Datei, in der
  // Summe und Teile einander widersprechen, ergibt so keinen Track, der beides
  // behauptet. Es ist dieselbe Rechnung wie beim Import einer GPX-Datei, also
  // dasselbe Ergebnis.
  const teile: TrackPart[] = []
  for (const entry of asArray(isRecord(v) ? v.parts : undefined)) {
    const kern = trackKern(entry)
    if (kern) teile.push({ ...kern, fingerprint: kern.fingerprint ?? '' })
  }
  if (teile.length > 0) return mergeTrackParts(teile) ?? ganzes

  return ganzes
}

/**
 * Ein Track oder ein Teil davon - beides hat dieselben Felder.
 *
 * Ein unbrauchbarer Stützpunkt verwirft den ganzen Track, genau wie bei der
 * berechneten Strasse: eine Linie, die einen Punkt überspringt, nimmt eine
 * Abkürzung, die niemand gefahren ist. Ein unbrauchbarer Halt dagegen fällt
 * nur selbst heraus - die Zeitleiste ist eine Aufzählung, und eine Zeile
 * weniger erfindet nichts.
 */
function trackKern(v: unknown): DiaryTrack | undefined {
  if (!isRecord(v)) return undefined

  const geometry: LatLon[] = []
  for (const point of asArray(v.geometry)) {
    const c = maybeCoord(point)
    if (!c) return undefined
    geometry.push(c)
  }
  if (geometry.length < 2) return undefined

  const distanceKm = num(v.distanceKm, NaN)
  if (!Number.isFinite(distanceKm) || distanceKm < 0) return undefined

  const stops: TrackStop[] = []
  for (const entry of asArray(v.stops)) {
    const s = isRecord(entry) ? entry : {}
    const at = maybeCoord(s.at)
    const from = num(s.from, NaN)
    const to = num(s.to, NaN)
    if (!at || !Number.isFinite(from) || !Number.isFinite(to) || to < from) continue
    stops.push({ from, to, at })
  }

  // Die Punkte mit Uhrzeit: wo die Reisenden wann waren. Ein unbrauchbarer
  // fällt für sich heraus - sie sind eine Aufzählung von Stellen der Fahrt, und
  // eine Stelle weniger erfindet nichts.
  const fixes: TrackFix[] = []
  for (const entry of asArray(v.fixes)) {
    const f = isRecord(entry) ? entry : {}
    const at = maybeCoord(f.at)
    const t = num(f.t, NaN)
    if (!at || !Number.isFinite(t)) continue
    fixes.push({ at, t })
  }

  const elevation = trackElevation(v.elevation)
  const totalSeconds = Math.max(0, num(v.totalSeconds, 0))
  const stoppedSeconds = Math.min(totalSeconds, Math.max(0, num(v.stoppedSeconds, 0)))
  // Die Zeit, über die nichts gemessen wurde. Fehlt in Dateien aus der Zeit vor
  // dem Feld und in jedem Track aus einer einzigen Aufzeichnung - dann ist sie
  // null und die Fahrzeit wie bisher der ganze Rest.
  const unrecordedSeconds = Math.min(
    totalSeconds - stoppedSeconds,
    Math.max(0, num(v.unrecordedSeconds, 0)),
  )

  return {
    geometry,
    distanceKm,
    startedAt: zeitpunkt(v.startedAt),
    endedAt: zeitpunkt(v.endedAt),
    totalSeconds,
    // Die Zeiten gehören zusammen: die Fahrzeit ist der Rest. Aus der Datei
    // übernommen, könnten sie einander widersprechen.
    movingSeconds: totalSeconds - stoppedSeconds - unrecordedSeconds,
    stoppedSeconds,
    ...(unrecordedSeconds > 0 ? { unrecordedSeconds } : {}),
    stops,
    ...(fixes.length > 0 ? { fixes } : {}),
    ...(elevation ? { elevation } : {}),
    pointCount: Math.max(0, Math.round(num(v.pointCount, geometry.length))),
    segments: Math.max(1, Math.round(num(v.segments, 1))),
    dropped: Math.max(0, Math.round(num(v.dropped, 0))),
    fileName: str(v.fileName),
    importedAt: num(v.importedAt, 0),
    // Leer heisst "unbekannt": so sehen Tracks aus Dateien aus, die älter sind
    // als das Feld. Dann entscheidet allein der Zeitbereich, ob eine Datei
    // schon einmal eingelesen wurde, siehe trackKonflikt in lib/gpx.ts.
    ...(str(v.fingerprint) ? { fingerprint: str(v.fingerprint) } : {}),
  }
}

/** Ein Höhenprofil aus einer Importdatei, oder nichts. */
function trackElevation(v: unknown): TrackElevation | undefined {
  if (!isRecord(v)) return undefined

  const profile: TrackProfilePoint[] = []
  for (const entry of asArray(v.profile)) {
    if (!Array.isArray(entry) || entry.length < 2) continue
    const km = num(entry[0], NaN)
    const ele = num(entry[1], NaN)
    if (!Number.isFinite(km) || !Number.isFinite(ele)) continue
    profile.push([km, ele])
  }

  const min = num(v.min, NaN)
  const max = num(v.max, NaN)
  const gain = num(v.gain, NaN)
  if (!Number.isFinite(min) || !Number.isFinite(max) || !Number.isFinite(gain)) return undefined

  return { profile, min, max, gain }
}

/**
 * Zeitstempel oder null - fehlende Zeitstempel sind bei einem Track normal.
 *
 * Die Prüfung auf null und die leere Zeichenkette steht ausdrücklich davor:
 * Number(null) und Number('') sind 0, und das wäre nicht "keine Zeit", sondern
 * der 1. Januar 1970.
 */
function zeitpunkt(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null
  const ts = num(v, NaN)
  return Number.isFinite(ts) ? ts : null
}

/**
 * Ein Tagebuchbeitrag aus einer Importdatei.
 *
 * Ein fehlender Verfasser ist kein Grund, den Beitrag zu verwerfen: der Text
 * ist das, was jemand aufgeschrieben hat, und der zählt auch ohne Namen. Er
 * bleibt dann eben ein Beitrag, den niemand ändern darf - siehe darfBearbeiten
 * in lib/diary.ts. Umgekehrt fällt ein Beitrag ohne Text weg (hasText): ihn
 * einzulesen hiesse, im Buch eine Verfasserzeile über nichts zu setzen.
 *
 * Fehlende Zeitpunkte werden zu 0 und nicht zu "jetzt". 0 heisst "unbekannt";
 * "jetzt" hiesse, dass die Reihenfolge der Beiträge davon abhängt, wann jemand
 * eine Datei eingelesen hat.
 */
function parsePost(raw: unknown): DiaryPost {
  const p = isRecord(raw) ? raw : {}
  const createdAt = Math.max(0, num(p.createdAt, 0))
  return {
    id: str(p.id) || newId('b'),
    stageId: str(p.stageId),
    authorId: str(p.authorId),
    authorName: str(p.authorName),
    createdAt,
    // Geändert kann nichts vor dem Anlegen worden sein. Eine Datei, die das
    // behauptet, bekommt hier den Zeitpunkt des Anlegens.
    updatedAt: Math.max(createdAt, num(p.updatedAt, createdAt)),
    text: str(p.text),
  }
}

function parseExpense(raw: unknown, trip: Trip): Expense {
  const e = isRecord(raw) ? raw : {}
  return {
    id: str(e.id) || newId('e'),
    // Eine Datei kann aus der Zeit stammen, in der die Kategorie noch anders
    // hiess. Der Import ist die günstigste Stelle, das zu bereinigen: was
    // hereinkommt, ist danach im heutigen Modell.
    category: currentCategory(str(e.category) || 'Sonstiges', trip),
    amount: num(e.amount, 0),
    note: str(e.note),
    stageId: str(e.stageId),
    date: str(e.date),
  }
}

function parsePhoto(raw: unknown): TransferPhoto {
  const p = isRecord(raw) ? raw : {}
  return {
    id: str(p.id) || newId('p'),
    stageId: str(p.stageId),
    createdAt: num(p.createdAt, Date.now()),
    dataUrl: str(p.dataUrl),
  }
}

function hasStage(d: DiaryEntry): boolean {
  return d.stageId !== ''
}

/** Ein Beitrag ohne Etappe oder ohne Text gehört in kein Buch. */
function hasText(p: DiaryPost): boolean {
  return p.stageId !== '' && p.text.trim() !== ''
}

function hasDataUrl(p: TransferPhoto): boolean {
  return p.dataUrl.startsWith('data:')
}

function coord(v: unknown): LatLon {
  return maybeCoord(v) ?? [0, 0]
}

/**
 * Ein Übernachtungsplatz aus einer Importdatei.
 *
 * Die Koordinate ist optional und wird nur übernommen, wenn sie einen Ort auf
 * der Erde bezeichnet. Anders als bei den Textfeldern gibt es hier keinen
 * Ersatzwert: eine 0/0 im Golf von Guinea wäre keine fehlende Angabe, sondern
 * eine falsche - und ein Platz an der falschen Stelle führt nachts an den
 * falschen Ort. Ohne Koordinate steht der Platz im Navigationsexport wieder am
 * Etappenziel, mit dem Hinweis, dass das nicht seine Adresse ist. Der Eintrag
 * selbst bleibt in jedem Fall erhalten.
 */
function campingPlatz(raw: unknown): Camping {
  const item = isRecord(raw) ? raw : {}
  const at = platzKoordinate(item.at)
  return {
    name: str(item.name),
    price: str(item.price),
    note: str(item.note),
    ...(at ? { at } : {}),
  }
}

/**
 * Die Koordinate eines Platzes, oder nichts.
 *
 * Strenger gelesen als die Etappenkoordinaten (maybeCoord): hier müssen es
 * Zahlen sein und keine Zeichenketten, die sich zu Zahlen machen lassen. Der
 * Grund ist ein einziger Wert - Number('') ist 0, und die Breite 0 ist keine
 * fehlende Angabe, sondern der Äquator. Ein Platz mit leerem Breitenfeld läge
 * damit im Golf von Guinea, und zwar mit einer Länge, die plausibel aussieht.
 * Bei c1 und c2 fällt so etwas auf der Karte sofort auf; ein Übernachtungsplatz
 * dagegen wird erst abends angefahren.
 */
function platzKoordinate(v: unknown): LatLon | null {
  if (!Array.isArray(v)) return null
  const [lat, lon] = v
  if (typeof lat !== 'number' || typeof lon !== 'number') return null
  return usablePlaceCoord(lat, lon) ? [lat, lon] : null
}

/** Wie coord, aber ohne Ersatzwert: null heisst "keine Koordinate". */
function maybeCoord(v: unknown): LatLon | null {
  if (Array.isArray(v) && v.length >= 2) {
    const lat = num(v[0], NaN)
    const lon = num(v[1], NaN)
    if (Number.isFinite(lat) && Number.isFinite(lon)) return [lat, lon]
  }
  return null
}

/**
 * Die berechnete Strecke einer Etappe aus einer Importdatei.
 *
 * Fehlt sie, ist es keine: Dateien aus der Zeit vor dem Feld kennen es nicht,
 * und eine Etappe ohne Strecke ist ein voelig normaler Zustand - die Karte
 * zeichnet dann die Luftlinie.
 *
 * Ist sie beschädigt, wird sie ganz verworfen und nicht zurechtgebogen. Ein
 * Ersatzwert wäre hier anders als bei den übrigen Feldern gefährlich: eine
 * Koordinate [0, 0] im Golf von Guinea macht aus der Strecke eine Linie quer
 * über die Karte, und eine Entfernung von 0 km stünde im Vergleich mit der
 * Planung als Auskunft da. Die Etappe selbst bleibt in jedem Fall erhalten.
 */
function stageRoute(v: unknown): StageRoute | undefined {
  if (!isRecord(v)) return undefined

  const geometry: LatLon[] = []
  for (const point of asArray(v.geometry)) {
    const c = maybeCoord(point)
    // Ein einzelner unbrauchbarer Stützpunkt macht die ganze Strecke
    // unbrauchbar: ihn stillschweigend zu überspringen ergäbe eine Linie, die
    // eine Abkürzung nimmt, die es auf der Strasse nicht gibt.
    if (!c) return undefined
    geometry.push(c)
  }
  if (geometry.length < 2) return undefined

  const from = maybeCoord(v.from)
  const to = maybeCoord(v.to)
  if (!from || !to) return undefined

  // Die Zwischenpunkte, über die gerechnet wurde. Ein unbrauchbarer verwirft
  // die ganze Strecke, genau wie ein unbrauchbarer Stützpunkt: an dieser Liste
  // hängt die Frage, ob die Strecke noch zur Etappe passt. Eine Liste, aus der
  // still ein Punkt fehlt, behauptete eine Strecke über den Pass, die in
  // Wahrheit durch den Tunnel führt - und dann als aktuell durchginge.
  const via: LatLon[] = []
  for (const point of asArray(v.via)) {
    const c = maybeCoord(point)
    if (!c) return undefined
    via.push(c)
  }

  const distanceKm = num(v.distanceKm, NaN)
  const durationH = num(v.durationH, NaN)
  if (!Number.isFinite(distanceKm) || !Number.isFinite(durationH)) return undefined

  // Die Fassung des Ausdünnverfahrens wird durchgereicht, nicht ersetzt: fehlt
  // sie, ist es eine Strecke aus der Zeit davor, und der nächste Sammelaufruf
  // holt sie sich genauer (isCoarseRoute in lib/route.ts).
  const detail = num(v.detail, 0)

  // Die Kehrenfolgen kommen mit, sofern sie plausibel sind. Anders als bei den
  // Stützpunkten reisst ein unbrauchbarer Eintrag hier nicht die Strecke mit:
  // eine fehlende Kehrenzeile ist ein Vorschlag weniger, eine fehlende
  // Koordinate ist eine erfundene Abkürzung. Nachrechnen lässt sich nichts -
  // die volle Geometrie, aus der gezählt wurde, ist in keiner Datei.
  const serpentines: SerpentineRun[] = []
  for (const eintrag of asArray(v.serpentines)) {
    if (!isRecord(eintrag)) continue
    const km = num(eintrag.km, NaN)
    const count = num(eintrag.count, NaN)
    const lengthKm = num(eintrag.lengthKm, NaN)
    if (!Number.isFinite(km) || km < 0) continue
    if (!Number.isFinite(count) || count < 1) continue
    if (!Number.isFinite(lengthKm) || lengthKm < 0) continue
    serpentines.push({ km, count: Math.round(count), lengthKm })
  }

  // Der Belag kommt mit, sofern er sich prüfen lässt. Wie bei den Kehren
  // reisst ein unbrauchbarer Eintrag die Strecke nicht mit: eine fehlende
  // Belagszeile ist eine Zeile weniger, eine fehlende Koordinate ist eine
  // erfundene Abkürzung. Fehlt er ganz, heisst das "noch nicht nachgesehen" -
  // und ausdrücklich nicht "durchgehend befestigt".
  const surface = surfaceFromRaw(v.surface)

  return {
    geometry,
    distanceKm,
    durationH,
    computedAt: num(v.computedAt, 0),
    ...(detail > 0 ? { detail } : {}),
    ...(serpentines.length > 0 ? { serpentines } : {}),
    ...(surface ? { surface } : {}),
    from,
    ...(via.length > 0 ? { via } : {}),
    to,
  }
}

/**
 * Die Zwischenpunkte einer Etappe aus einer Importdatei.
 *
 * Ein Eintrag ohne brauchbaren Ort fällt für sich heraus und reisst nicht die
 * ganze Liste mit - anders als bei der berechneten Strecke, und der
 * Unterschied hat einen Grund. Die Strecke ist eine gezeichnete Linie: wer aus
 * ihr einen Punkt entfernt, erfindet eine Abkürzung, die es auf der Strasse
 * nicht gibt, und sie wird als Tatsache angezeigt. Die Zwischenpunkte sind
 * dagegen eine Vorgabe für die nächste Berechnung, und die gespeicherte
 * Strecke trägt ihre eigene Liste mit sich: fehlt hier ein Punkt, passt sie
 * nicht mehr dazu, gilt als veraltet und wird gar nicht erst gezeichnet. Der
 * Fehler wird also sichtbar, statt sich als gültige Linie zu tarnen - und die
 * übrigen Punkte, die jemand von Hand eingetragen hat, bleiben erhalten.
 *
 * Geprüft wird streng wie bei der Koordinate eines Platzes: es müssen Zahlen
 * sein und keine Zeichenketten, die sich zu Zahlen machen lassen. Number('')
 * ist 0, und die Breite 0 ist keine fehlende Angabe, sondern der Äquator - ein
 * Zwischenpunkt im Golf von Guinea führte die Strecke quer über die Karte.
 */
function stageVia(v: unknown): Waypoint[] {
  const punkte: Waypoint[] = []
  for (const entry of asArray(v)) {
    const item = isRecord(entry) ? entry : {}
    const at = platzKoordinate(item.at)
    if (!at) continue
    punkte.push({ name: str(item.name), at })
  }
  return punkte.slice(0, MAX_VIA)
}

/**
 * Die Tulpen einer Etappe aus einer Importdatei.
 *
 * Anders als bei der berechneten Strecke fällt hier ein unbrauchbarer Eintrag
 * nur selbst heraus und reisst nicht die ganze Liste mit: eine Tulpenliste ist
 * eine Aufzählung von Stellen, keine zusammenhängende Linie. Eine Zeile
 * weniger heisst "diese Stelle steht nicht im Buch"; eine verworfene Liste
 * hiesse, dass eine Etappe, deren Roadbook jemand unterwegs gefüllt hat, nach
 * dem Import wieder leer ist.
 *
 * Eine unbekannte Form macht den Eintrag unbrauchbar und wird nicht auf die
 * Kreuzung zurechtgebogen: eine Skizze, die eine Kreuzung behauptet, wo in der
 * Datei etwas anderes stand, ist schlechter als keine Skizze.
 */
function stageTulips(v: unknown): Tulip[] {
  const tulips: Tulip[] = []

  for (const entry of asArray(v)) {
    const t = isRecord(entry) ? entry : {}
    if (!isTulipShape(t.shape)) continue

    // Die Herkunft nur, wenn sie sich als Stelle lesen lässt. Ein Schlüssel,
    // den niemand deuten kann, hielte sonst stumm einen Vorschlag aus dem Buch
    // heraus - und wonach, wüsste dann keiner.
    const herkunft = stelleAus(str(t.src)) ? str(t.src) : ''

    tulips.push({
      km: kilometerstand(t.km),
      shape: t.shape,
      turn: normalizeTurn(num(t.turn, 0)),
      note: str(t.note),
      ...(herkunft ? { src: herkunft } : {}),
    })
  }

  return tulips
}

/**
 * Die abgelehnten Vorschlagsstellen einer Etappe aus einer Importdatei.
 *
 * Gelesen wird nur, was sich als Stelle deuten lässt (lib/tulipMerge.ts); alles
 * andere fällt einzeln heraus. Dieselbe Regel wie bei den Tulpen selbst: die
 * Liste ist eine Aufzählung und keine zusammenhängende Angabe, und ein
 * unlesbarer Eintrag darf die übrigen Absagen nicht mitnehmen.
 *
 * Doppelte fallen weg und der Deckel gilt auch hier - eine Datei von aussen
 * darf einer Etappe keine Liste mitbringen, die die App selbst nie erzeugen
 * würde.
 */
function stageTulipsRejected(v: unknown): string[] {
  const stellen: string[] = []

  for (const entry of asArray(v)) {
    const schluessel = str(entry)
    if (!stelleAus(schluessel) || stellen.includes(schluessel)) continue
    stellen.push(schluessel)
  }

  return stellen.slice(-MAX_ABSAGEN)
}

/**
 * Der Kilometerstand einer Tulpe, oder NaN für "noch nicht bekannt".
 *
 * Ein fehlender Stand ist kein Fehler: unterwegs wird die Zeile notiert, bevor
 * der Tacho abgelesen ist. Genau deshalb steht die Prüfung auf null und die
 * leere Zeichenkette ausdrücklich davor - Number(null) und Number('') sind 0,
 * und aus einem unbekannten Kilometerstand würde stillschweigend der Beginn
 * der Etappe. Dasselbe Fettnäpfchen wie bei den Zeitstempeln eines Tracks,
 * siehe zeitpunkt().
 *
 * Und NaN und nicht -1 oder null: das Feld ist eine Zahl, tulipKm macht daraus
 * einen Strich, und JSON trägt den Wert als null durch die Exportdatei - wo er
 * hier wieder als "unbekannt" ankommt.
 */
function kilometerstand(v: unknown): number {
  if (v === null || v === undefined || v === '') return NaN

  const km = num(v, NaN)
  return Number.isFinite(km) && km >= 0 ? km : NaN
}

function stageType(v: unknown): StageType {
  const s = str(v) as StageType
  return STAGE_TYPES.includes(s) ? s : 'kurz'
}
